import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PROFILE_SETUP } from "@/constants/testIds";
import { toast } from "sonner";
import { X, Upload, Lock, Eye, EyeOff } from "lucide-react";

const DENOMS = ["Greek Orthodox", "Russian Orthodox", "Antiochian Orthodox", "Romanian Orthodox", "Bulgarian Orthodox", "Malankara Orthodox", "Coptic Orthodox", "Serbian Orthodox", "Georgian Orthodox", "Other Orthodox"];

export default function ProfileSetup() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [form, setForm] = useState({
        full_name: "", gender: "", date_of_birth: "", height_cm: "",
        marital_status: "Never Married", religion: "Christian", denomination: "", mother_tongue: "",
        city: "", country: "", education: "", profession: "", annual_income: "",
        about_me: "", family_type: "", father_occupation: "", mother_occupation: "", siblings: "",
        pref_age_min: "", pref_age_max: "", pref_height_min: "", pref_denomination: "", pref_location: "", pref_education: "",
        hide_photo: false, hide_contact: true,
    });
    const [photos, setPhotos] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await client.get("/profiles/me");
                setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(data).filter(([k, v]) => v != null && k in f)) }));
                setPhotos(data.photos || []);
            } catch { /* unauthenticated or no profile yet */ }
        };
        load();
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await client.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPhotos((p) => [...p, data.url]);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removePhoto = async (url) => {
        try {
            await client.delete(`/photos?url=${encodeURIComponent(url)}`);
            setPhotos((p) => p.filter((x) => x !== url));
        } catch { toast.error("Failed to remove"); }
    };

    const save = async () => {
        setSaving(true);
        const payload = {};
        for (const [k, v] of Object.entries(form)) {
            if (v === "" || v == null) continue;
            if (["height_cm", "siblings", "pref_age_min", "pref_age_max", "pref_height_min"].includes(k)) {
                const n = parseInt(v, 10);
                if (!Number.isNaN(n)) payload[k] = n;
            } else {
                payload[k] = v;
            }
        }
        try {
            await client.put("/profiles/me", payload);
            await refresh();
            toast.success("Profile saved");
            navigate("/dashboard");
        } catch (e) {
            toast.error("Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">My Profile</div>
            <h1 className="mt-2 font-serif text-5xl text-navy tracking-tighter">Tell us about yourself</h1>
            <p className="mt-3 text-navy-500 font-sans max-w-2xl">Sharing more helps faithful matches discover you. All fields are optional but recommended.</p>

            {/* Photos */}
            <Section title="Photos" subtitle="Upload up to 6 photos. First photo becomes your primary.">
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {photos.map((p) => (
                        <div key={p} className="relative aspect-square rounded-xl overflow-hidden bg-linen group">
                            <img src={p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`} alt="" className="w-full h-full object-cover" />
                            <button
                                onClick={() => removePhoto(p)}
                                data-testid={PROFILE_SETUP.deletePhoto}
                                className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
                            ><X className="h-3.5 w-3.5 text-navy" /></button>
                        </div>
                    ))}
                    {photos.length < 6 && (
                        <button
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid={PROFILE_SETUP.photoUpload}
                            className="aspect-square rounded-xl border-2 border-dashed border-navy-200 hover:border-gold hover:bg-linen flex flex-col items-center justify-center gap-1 text-navy-400 hover:text-navy transition-all"
                        >
                            <Upload className="h-5 w-5" />
                            <span className="text-[10px] uppercase tracking-wider font-sans">{uploading ? "Uploading…" : "Add Photo"}</span>
                        </button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
                </div>
            </Section>

            <Section title="Personal Details">
                <Grid>
                    <Field label="Full Name"><Input value={form.full_name} onChange={(v) => set("full_name", v)} /></Field>
                    <Field label="Gender">
                        <Selecter value={form.gender} onChange={(v) => set("gender", v)} options={[["male","Male"],["female","Female"]]} />
                    </Field>
                    <Field label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} /></Field>
                    <Field label="Height (cm)"><Input type="number" value={form.height_cm} onChange={(v) => set("height_cm", v)} placeholder="170" /></Field>
                    <Field label="Marital Status">
                        <Selecter value={form.marital_status} onChange={(v) => set("marital_status", v)} options={[["Never Married","Never Married"],["Divorced","Divorced"],["Widowed","Widowed"]]} />
                    </Field>
                    <Field label="Mother Tongue"><Input value={form.mother_tongue} onChange={(v) => set("mother_tongue", v)} placeholder="English" /></Field>
                </Grid>
            </Section>

            <Section title="Faith">
                <Grid>
                    <Field label="Religion"><Input value={form.religion} onChange={(v) => set("religion", v)} /></Field>
                    <Field label="Denomination">
                        <Selecter value={form.denomination} onChange={(v) => set("denomination", v)} options={DENOMS.map((d) => [d, d])} />
                    </Field>
                </Grid>
            </Section>

            <Section title="Location">
                <Grid>
                    <Field label="City"><Input value={form.city} onChange={(v) => set("city", v)} /></Field>
                    <Field label="Country"><Input value={form.country} onChange={(v) => set("country", v)} /></Field>
                </Grid>
            </Section>

            <Section title="Education & Career">
                <Grid>
                    <Field label="Education"><Input value={form.education} onChange={(v) => set("education", v)} placeholder="MA, BSc, etc." /></Field>
                    <Field label="Profession"><Input value={form.profession} onChange={(v) => set("profession", v)} placeholder="Software Engineer" /></Field>
                    <Field label="Annual Income"><Input value={form.annual_income} onChange={(v) => set("annual_income", v)} placeholder="$80,000" /></Field>
                </Grid>
            </Section>

            <Section title="Family Background">
                <Grid>
                    <Field label="Family Type">
                        <Selecter value={form.family_type} onChange={(v) => set("family_type", v)} options={[["Nuclear","Nuclear"],["Joint","Joint"]]} />
                    </Field>
                    <Field label="Father's Occupation"><Input value={form.father_occupation} onChange={(v) => set("father_occupation", v)} /></Field>
                    <Field label="Mother's Occupation"><Input value={form.mother_occupation} onChange={(v) => set("mother_occupation", v)} /></Field>
                    <Field label="Siblings"><Input type="number" value={form.siblings} onChange={(v) => set("siblings", v)} /></Field>
                </Grid>
            </Section>

            <Section title="About Me">
                <textarea
                    value={form.about_me} onChange={(e) => set("about_me", e.target.value)}
                    rows={5} placeholder="Share your faith, your hopes for marriage, your interests..."
                    className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-4 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all"
                />
            </Section>

            <Section title="Partner Preferences">
                <Grid>
                    <Field label="Age (min)"><Input type="number" value={form.pref_age_min} onChange={(v) => set("pref_age_min", v)} /></Field>
                    <Field label="Age (max)"><Input type="number" value={form.pref_age_max} onChange={(v) => set("pref_age_max", v)} /></Field>
                    <Field label="Min Height (cm)"><Input type="number" value={form.pref_height_min} onChange={(v) => set("pref_height_min", v)} /></Field>
                    <Field label="Preferred Denomination">
                        <Selecter value={form.pref_denomination} onChange={(v) => set("pref_denomination", v)} options={DENOMS.map((d) => [d, d])} />
                    </Field>
                    <Field label="Preferred Location"><Input value={form.pref_location} onChange={(v) => set("pref_location", v)} /></Field>
                    <Field label="Preferred Education"><Input value={form.pref_education} onChange={(v) => set("pref_education", v)} /></Field>
                </Grid>
            </Section>

            <Section title="Privacy Controls" subtitle="Control who sees your details.">
                <div className="space-y-3">
                    <Toggle
                        icon={form.hide_photo ? <EyeOff /> : <Eye />}
                        label="Hide my photos from public" desc="Only logged-in members who match your preferences will see your photos."
                        checked={!!form.hide_photo} onChange={(v) => set("hide_photo", v)}
                    />
                    <Toggle
                        icon={<Lock />}
                        label="Hide contact details" desc="Email and phone are only visible to premium members you've accepted."
                        checked={!!form.hide_contact} onChange={(v) => set("hide_contact", v)}
                    />
                </div>
            </Section>

            <div className="mt-12 flex justify-end">
                <button
                    onClick={save} disabled={saving}
                    data-testid={PROFILE_SETUP.saveButton}
                    className="font-sans text-xs uppercase tracking-[0.18em] bg-navy text-white rounded-lg px-10 py-4 hover:bg-navy-700 active:scale-95 transition-all shadow-lg shadow-navy/20 disabled:opacity-50"
                >
                    {saving ? "Saving…" : "Save Profile"}
                </button>
            </div>
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="mt-10 bg-white border border-navy-100 rounded-2xl p-6 md:p-8">
            <div className="mb-5">
                <h2 className="font-serif text-2xl text-navy">{title}</h2>
                {subtitle && <p className="text-sm text-navy-500 font-sans">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}
function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs tracking-[0.18em] uppercase text-navy-500 font-semibold mb-2">{label}</label>
            {children}
        </div>
    );
}
function Input({ value, onChange, type = "text", placeholder }) {
    return (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-3.5 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all" />
    );
}
function Selecter({ value, onChange, options }) {
    return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}
            className="w-full bg-linen border border-navy-100 rounded-xl px-5 py-3.5 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans transition-all appearance-none">
            <option value="">— Select —</option>
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
    );
}
function Toggle({ icon, label, desc, checked, onChange }) {
    return (
        <label className="flex items-start gap-4 p-4 rounded-xl border border-navy-100 hover:bg-linen cursor-pointer transition-colors">
            <div className="h-10 w-10 rounded-full bg-linen text-gold-700 flex items-center justify-center shrink-0">{icon}</div>
            <div className="flex-1">
                <div className="font-sans font-semibold text-navy text-sm">{label}</div>
                <div className="text-xs text-navy-500 font-sans">{desc}</div>
            </div>
            <button type="button" onClick={() => onChange(!checked)}
                className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-gold" : "bg-navy-200"}`}>
                <span className={`absolute top-0.5 ${checked ? "right-0.5" : "left-0.5"} h-5 w-5 bg-white rounded-full transition-all shadow-sm`}></span>
            </button>
        </label>
    );
}
